import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, CheckCircle, Clock, CheckSquare, Square, Check, XCircle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper para formatear moneda con comas
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0.00';
  return '$' + Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const PayoutManagement = () => {
  const { token } = useAuth();
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [activeView, setActiveView] = useState('pending');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Bulk selection state
  const [selectedPayoutIds, setSelectedPayoutIds] = useState([]);
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [pendingRes, historyRes, requestsRes] = await Promise.all([
        axios.get(`${API}/admin/pending-payouts`, { headers }),
        axios.get(`${API}/admin/payout-history`, { headers }),
        axios.get(`${API}/admin/payment-requests`, { headers })
      ]);

      setPendingPayouts(pendingRes.data);
      setPayoutHistory(historyRes.data);
      setPaymentRequests(requestsRes.data);
    } catch (error) {
      console.error('Error fetching payout data:', error);
      toast.error('Error al cargar datos de pagos');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayout = async (payoutSummary, paymentMethod) => {
    try {
      const commissionIds = payoutSummary.commissions.map(c => c.id);
      
      const response = await axios.post(
        `${API}/admin/process-payout`,
        {
          commission_ids: commissionIds,
          payment_method: paymentMethod,
          notes: `Pago procesado vía ${paymentMethod.toUpperCase()}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Pago procesado: ${formatCurrency(response.data.total_amount)} MXN`);
      setShowPayoutModal(false);
      setSelectedPayout(null);
      fetchData();
    } catch (error) {
      console.error('Error processing payout:', error);
      toast.error('Error al procesar el pago');
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      const response = await axios.post(
        `${API}/admin/approve-payment-request/${requestId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Solicitud aprobada y procesada: ${formatCurrency(response.data.amount)} MXN`);
      fetchData();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error('Error al aprobar solicitud');
    }
  };

  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const url = `${API}/admin/export-payouts-csv?${params.toString()}`;
    window.open(url, '_blank');
    toast.success('Descargando reporte CSV...');
  };

  const handleExportPDF = () => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const url = `${API}/admin/export-payouts-pdf?${params.toString()}`;
    window.open(url, '_blank');
    toast.success('Descargando reporte PDF...');
  };

  // ============= BULK ACTIONS =============
  const togglePayoutSelection = (userId) => {
    setSelectedPayoutIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAllPayouts = () => {
    if (selectedPayoutIds.length === pendingPayouts.length) {
      setSelectedPayoutIds([]);
    } else {
      setSelectedPayoutIds(pendingPayouts.map(p => p.user_id));
    }
  };

  const toggleRequestSelection = (requestId) => {
    setSelectedRequestIds(prev =>
      prev.includes(requestId)
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  const toggleSelectAllRequests = () => {
    const pendingRequests = paymentRequests.filter(r => r.status === 'pending');
    if (selectedRequestIds.length === pendingRequests.length) {
      setSelectedRequestIds([]);
    } else {
      setSelectedRequestIds(pendingRequests.map(r => r.id));
    }
  };

  const handleBulkProcessPayouts = async (paymentMethod) => {
    if (selectedPayoutIds.length === 0) {
      toast.error('Selecciona al menos un usuario para procesar');
      return;
    }

    if (!window.confirm(`¿Procesar pagos de ${selectedPayoutIds.length} usuarios como ${paymentMethod.toUpperCase()}?`)) {
      return;
    }

    try {
      // Recopilar todas las comisiones de los usuarios seleccionados
      const commissionIds = [];
      let totalAmount = 0;
      
      for (const userId of selectedPayoutIds) {
        const payout = pendingPayouts.find(p => p.user_id === userId);
        if (payout?.commissions) {
          commissionIds.push(...payout.commissions.map(c => c.id));
          totalAmount += payout.total_pending || 0;
        }
      }

      const response = await axios.post(
        `${API}/admin/process-payout`,
        {
          commission_ids: commissionIds,
          payment_method: paymentMethod,
          notes: `Pago masivo procesado vía ${paymentMethod.toUpperCase()}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`✅ Pagos procesados: ${formatCurrency(response.data.total_amount || totalAmount)} MXN para ${selectedPayoutIds.length} usuarios`);
      setSelectedPayoutIds([]);
      fetchData();
    } catch (error) {
      console.error('Error in bulk payout:', error);
      toast.error('Error al procesar pagos masivos');
    }
  };

  const handleBulkApproveRequests = async () => {
    if (selectedRequestIds.length === 0) {
      toast.error('Selecciona al menos una solicitud');
      return;
    }

    if (!window.confirm(`¿Aprobar ${selectedRequestIds.length} solicitudes de pago?`)) {
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const requestId of selectedRequestIds) {
      try {
        await axios.post(
          `${API}/admin/approve-payment-request/${requestId}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        successCount++;
      } catch (error) {
        console.error(`Error approving request ${requestId}:`, error);
        failCount++;
      }
    }

    toast.success(`✅ Aprobadas: ${successCount}, Fallidas: ${failCount}`);
    setSelectedRequestIds([]);
    fetchData();
  };

  const handleBulkRejectRequests = async () => {
    if (selectedRequestIds.length === 0) {
      toast.error('Selecciona al menos una solicitud');
      return;
    }

    if (!window.confirm(`¿RECHAZAR ${selectedRequestIds.length} solicitudes de pago?`)) {
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const requestId of selectedRequestIds) {
      try {
        await axios.post(
          `${API}/admin/reject-payment-request/${requestId}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        successCount++;
      } catch (error) {
        console.error(`Error rejecting request ${requestId}:`, error);
        failCount++;
      }
    }

    toast.success(`❌ Rechazadas: ${successCount}, Fallidas: ${failCount}`);
    setSelectedRequestIds([]);
    fetchData();
  };

  const filteredHistory = payoutHistory.filter(payout => {
    if (!startDate && !endDate) return true;
    
    const payoutDate = new Date(payout.paid_at || payout.created_at);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    
    if (start && payoutDate < start) return false;
    if (end && payoutDate > end) return false;
    
    return true;
  });

  if (loading) {
    return (
      <Card className="card-viubux-dark border-purple-500/20">
        <CardContent className="py-12 text-center">
          <Clock className="w-12 h-12 mx-auto mb-4 text-purple-400 animate-spin" />
          <p className="verdana-regular text-white/70">Cargando datos de pagos...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-4 border-b border-purple-500/20">
        <button
          onClick={() => setActiveView('requests')}
          className={`gotham-medium px-4 py-2 transition-colors ${
            activeView === 'requests'
              ? 'text-white border-b-2 border-purple-500'
              : 'text-white/60 hover:text-white'
          }`}
          data-testid="tab-payment-requests"
        >
          Solicitudes ({paymentRequests.filter(r => r.status === 'pending').length})
        </button>
        <button
          onClick={() => setActiveView('pending')}
          className={`gotham-medium px-4 py-2 transition-colors ${
            activeView === 'pending'
              ? 'text-white border-b-2 border-purple-500'
              : 'text-white/60 hover:text-white'
          }`}
          data-testid="tab-pending-payouts"
        >
          Pagos Pendientes ({pendingPayouts.length})
        </button>
        <button
          onClick={() => setActiveView('history')}
          className={`gotham-medium px-4 py-2 transition-colors ${
            activeView === 'history'
              ? 'text-white border-b-2 border-purple-500'
              : 'text-white/60 hover:text-white'
          }`}
          data-testid="tab-payout-history"
        >
          Historial de Pagos
        </button>
      </div>

      {/* Payment Requests */}
      {activeView === 'requests' && (
        <Card className="card-viubux-dark border-purple-500/20" data-testid="payment-requests-card">
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <CardTitle className="gotham-bold text-white flex items-center gap-2">
                  <Clock className="w-5 h-5" style={{ color: '#7012b2' }} />
                  Solicitudes de Pago de Usuarios
                  {selectedRequestIds.length > 0 && (
                    <span className="text-yellow-400 text-sm font-normal">• {selectedRequestIds.length} seleccionadas</span>
                  )}
                </CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Usuarios que han solicitado cobrar sus comisiones
                </CardDescription>
              </div>
              {/* Bulk actions for requests */}
              {selectedRequestIds.length > 0 && (
                <div className="flex gap-2 p-2 bg-purple-500/10 rounded-lg border border-purple-500/30">
                  <Button
                    size="sm"
                    onClick={handleBulkApproveRequests}
                    className="bg-green-600 hover:bg-green-500 text-white text-xs"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Aprobar Todas
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleBulkRejectRequests}
                    className="bg-red-600 hover:bg-red-500 text-white text-xs"
                  >
                    <XCircle className="w-3 h-3 mr-1" />
                    Rechazar Todas
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {paymentRequests.filter(r => r.status === 'pending').length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                <p className="verdana-regular text-white/70">
                  No hay solicitudes pendientes
                </p>
              </div>
            ) : (
              <>
                {/* Select all header */}
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-purple-500/20">
                  <button
                    onClick={toggleSelectAllRequests}
                    className="p-1 hover:bg-purple-500/20 rounded"
                    title={selectedRequestIds.length === paymentRequests.filter(r => r.status === 'pending').length ? "Deseleccionar todas" : "Seleccionar todas"}
                  >
                    {selectedRequestIds.length === paymentRequests.filter(r => r.status === 'pending').length && paymentRequests.filter(r => r.status === 'pending').length > 0 ? (
                      <CheckSquare className="w-5 h-5 text-purple-400" />
                    ) : (
                      <Square className="w-5 h-5 text-white/50" />
                    )}
                  </button>
                  <span className="text-white/60 text-sm">Seleccionar todas</span>
                </div>
                <div className="space-y-4">
                  {paymentRequests.filter(r => r.status === 'pending').map((request, idx) => {
                    const isSelected = selectedRequestIds.includes(request.id);
                    return (
                      <div
                        key={idx}
                        className={`bg-yellow-500/10 border rounded-lg p-4 ${
                          isSelected ? 'border-purple-500/50 bg-purple-500/10' : 'border-yellow-500/30'
                        }`}
                        data-testid={`payment-request-${idx}`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <button
                            onClick={() => toggleRequestSelection(request.id)}
                            className="p-1 hover:bg-purple-500/20 rounded mt-1"
                          >
                            {isSelected ? (
                              <CheckSquare className="w-5 h-5 text-purple-400" />
                            ) : (
                              <Square className="w-5 h-5 text-white/30" />
                            )}
                          </button>
                          
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h3 className="gotham-bold text-white text-lg">{request.user_name}</h3>
                                <p className="verdana-regular text-white/70 text-sm">{request.user_email}</p>
                                <p className="verdana-regular text-white/60 text-xs mt-1">
                                  Solicitado: {new Date(request.created_at).toLocaleString('es-MX')}
                                </p>
                              </div>
                              <div className="text-right">
                                <div className="gotham-black text-2xl text-yellow-400">
                                  {formatCurrency(request.amount)}
                                </div>
                                <div className="verdana-regular text-white/60 text-xs">
                                  MXN
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-3">
                              <div className="bg-black/30 rounded p-2">
                                <p className="verdana-regular text-white/60 text-xs">Método:</p>
                                <p className="gotham-bold text-white uppercase">
                                  {request.payment_method || 'No configurado'}
                                </p>
                              </div>
                              <div className="bg-black/30 rounded p-2">
                                <p className="verdana-regular text-white/60 text-xs">Cuenta:</p>
                                <p className="verdana-regular text-white text-sm font-mono break-all">
                                  {request.payment_account || 'N/A'}
                                </p>
                              </div>
                            </div>

                            <Button
                              onClick={() => handleApproveRequest(request.id)}
                              className="w-full btn-viubux"
                              data-testid={`approve-request-${idx}`}
                            >
                              Aprobar y Procesar Pago
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pending Payouts */}
      {activeView === 'pending' && (
        <Card className="card-viubux-dark border-purple-500/20" data-testid="pending-payouts-card">
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <CardTitle className="gotham-bold text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5" style={{ color: '#7012b2' }} />
                  Comisiones Pendientes de Pago
                  {selectedPayoutIds.length > 0 && (
                    <span className="text-green-400 text-sm font-normal">• {selectedPayoutIds.length} seleccionados</span>
                  )}
                </CardTitle>
                <CardDescription className="verdana-regular text-white/70">
                  Usuarios con comisiones acumuladas esperando pago
                </CardDescription>
              </div>
              {/* Bulk actions for pending payouts */}
              {selectedPayoutIds.length > 0 && (
                <div className="flex flex-wrap gap-2 p-2 bg-green-500/10 rounded-lg border border-green-500/30">
                  <Button
                    size="sm"
                    onClick={() => handleBulkProcessPayouts('spei')}
                    className="bg-blue-600 hover:bg-blue-500 text-white text-xs"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Pagar SPEI
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleBulkProcessPayouts('paypal')}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Pagar PayPal
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleBulkProcessPayouts('efectivo')}
                    className="bg-green-600 hover:bg-green-500 text-white text-xs"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Pagar Efectivo
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {pendingPayouts.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                <p className="verdana-regular text-white/70">
                  ¡Todo al día! No hay pagos pendientes.
                </p>
              </div>
            ) : (
              <>
                {/* Select all header */}
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-purple-500/20">
                  <button
                    onClick={toggleSelectAllPayouts}
                    className="p-1 hover:bg-purple-500/20 rounded"
                    title={selectedPayoutIds.length === pendingPayouts.length ? "Deseleccionar todos" : "Seleccionar todos"}
                  >
                    {selectedPayoutIds.length === pendingPayouts.length && pendingPayouts.length > 0 ? (
                      <CheckSquare className="w-5 h-5 text-green-400" />
                    ) : (
                      <Square className="w-5 h-5 text-white/50" />
                    )}
                  </button>
                  <span className="text-white/60 text-sm">Seleccionar todos ({pendingPayouts.length})</span>
                </div>
                <div className="space-y-4">
                  {pendingPayouts.map((payout, idx) => {
                    const isSelected = selectedPayoutIds.includes(payout.user_id);
                    return (
                      <div
                        key={idx}
                        className={`border rounded-lg p-4 ${
                          isSelected ? 'bg-green-500/15 border-green-500/50' : 'bg-purple-500/10 border-purple-500/30'
                        }`}
                        data-testid={`pending-payout-${idx}`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <button
                            onClick={() => togglePayoutSelection(payout.user_id)}
                            className="p-1 hover:bg-purple-500/20 rounded mt-1"
                          >
                            {isSelected ? (
                              <CheckSquare className="w-5 h-5 text-green-400" />
                            ) : (
                              <Square className="w-5 h-5 text-white/30" />
                            )}
                          </button>
                          
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h3 className="gotham-bold text-white text-lg">{payout.user_name}</h3>
                                <p className="verdana-regular text-white/70 text-sm">{payout.user_email}</p>
                              </div>
                              <div className="text-right">
                                <div className="gotham-black text-2xl text-green-400">
                                  {formatCurrency(payout.total_pending)}
                                </div>
                                <div className="verdana-regular text-white/60 text-xs">
                                  MXN
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-3">
                              <div className="bg-black/30 rounded p-2">
                                <p className="verdana-regular text-white/60 text-xs">Comisiones:</p>
                                <p className="gotham-bold text-white">{payout.commission_count}</p>
                              </div>
                              <div className="bg-black/30 rounded p-2">
                                <p className="verdana-regular text-white/60 text-xs">Método de pago:</p>
                                <p className="gotham-bold text-white">
                                  {payout.payment_method ? payout.payment_method.toUpperCase() : 'No configurado'}
                                </p>
                              </div>
                            </div>

                            {payout.payment_account && (
                              <div className="bg-black/30 rounded p-2 mb-3">
                                <p className="verdana-regular text-white/60 text-xs">Cuenta:</p>
                                <p className="verdana-regular text-white text-sm font-mono">{payout.payment_account}</p>
                              </div>
                            )}

                            {!payout.payment_method || !payout.payment_account ? (
                              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2 mb-3">
                                <p className="verdana-regular text-yellow-200 text-xs">
                                  ⚠️ Usuario sin cuenta de pago configurada
                                </p>
                              </div>
                            ) : null}

                            <Button
                              onClick={() => {
                                setSelectedPayout(payout);
                                setShowPayoutModal(true);
                              }}
                              className="w-full btn-viubux"
                              disabled={!payout.payment_method || !payout.payment_account}
                              data-testid={`process-payout-btn-${idx}`}
                            >
                              Procesar Pago
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payout History */}
      {activeView === 'history' && (
        <Card className="card-viubux-dark border-purple-500/20" data-testid="payout-history-card">
          <CardHeader>
            <CardTitle className="gotham-bold text-white">Historial de Pagos</CardTitle>
            <CardDescription className="verdana-regular text-white/70">
              Registro de todos los pagos procesados
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Filters and Export */}
            <div className="mb-6 flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="verdana-regular text-white/80 text-sm mb-2 block">
                  Fecha Inicio
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-black/30 border border-purple-500/30 text-white verdana-regular focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="filter-start-date"
                />
              </div>
              
              <div className="flex-1 min-w-[200px]">
                <label className="verdana-regular text-white/80 text-sm mb-2 block">
                  Fecha Fin
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-black/30 border border-purple-500/30 text-white verdana-regular focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="filter-end-date"
                />
              </div>
              
              <Button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                }}
                variant="outline"
                className="border-purple-500/50 text-white hover:bg-purple-500/20"
                data-testid="clear-filters-btn"
              >
                Limpiar Filtros
              </Button>
              
              <Button
                onClick={handleExportCSV}
                variant="outline"
                className="border-purple-500/50 text-white hover:bg-purple-500/20"
                data-testid="export-csv-btn"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                CSV
              </Button>
              
              <Button
                onClick={handleExportPDF}
                className="btn-viubux"
                data-testid="export-pdf-btn"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                PDF
              </Button>
            </div>

            {filteredHistory.length === 0 ? (
              <div className="text-center py-12 text-white/60 verdana-regular">
                No hay pagos registrados aún
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full verdana-regular">
                  <thead>
                    <tr className="border-b border-purple-500/20">
                      <th className="text-left py-3 px-4 text-white/80">Fecha</th>
                      <th className="text-left py-3 px-4 text-white/80">Usuario</th>
                      <th className="text-left py-3 px-4 text-white/80">Método</th>
                      <th className="text-left py-3 px-4 text-white/80">Cuenta</th>
                      <th className="text-left py-3 px-4 text-white/80">Comisiones</th>
                      <th className="text-left py-3 px-4 text-white/80">Total</th>
                      <th className="text-left py-3 px-4 text-white/80">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.map((payout, idx) => (
                      <tr key={idx} className="border-b border-purple-500/10 hover:bg-purple-500/5" data-testid={`history-row-${idx}`}>
                        <td className="py-3 px-4 text-white/70">
                          {new Date(payout.paid_at || payout.created_at).toLocaleDateString('es-MX')}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-white">{payout.user_name}</div>
                          <div className="text-white/60 text-xs">{payout.user_email}</div>
                        </td>
                        <td className="py-3 px-4 text-white/70 uppercase">{payout.payment_method}</td>
                        <td className="py-3 px-4 text-white/70 font-mono text-xs">{payout.payment_account}</td>
                        <td className="py-3 px-4 text-white">{payout.commission_ids.length}</td>
                        <td className="py-3 px-4 gotham-bold text-green-400">{formatCurrency(payout.total_amount)}</td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
                            {payout.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payout Modal */}
      {showPayoutModal && selectedPayout && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="payout-modal">
          <div className="bg-gray-900 border border-purple-500/30 rounded-2xl max-w-md w-full p-6">
            <h3 className="gotham-bold text-2xl text-white mb-4">Confirmar Pago</h3>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between verdana-regular">
                <span className="text-white/70">Usuario:</span>
                <span className="text-white">{selectedPayout.user_name}</span>
              </div>
              <div className="flex justify-between verdana-regular">
                <span className="text-white/70">Email:</span>
                <span className="text-white">{selectedPayout.user_email}</span>
              </div>
              <div className="flex justify-between verdana-regular">
                <span className="text-white/70">Método:</span>
                <span className="text-white uppercase">{selectedPayout.payment_method}</span>
              </div>
              <div className="flex justify-between verdana-regular">
                <span className="text-white/70">Cuenta:</span>
                <span className="text-white font-mono text-sm">{selectedPayout.payment_account}</span>
              </div>
              <div className="flex justify-between verdana-regular">
                <span className="text-white/70">Comisiones:</span>
                <span className="text-white">{selectedPayout.commission_count}</span>
              </div>
              <div className="flex justify-between verdana-regular">
                <span className="text-white/70">Total a pagar:</span>
                <span className="gotham-black text-2xl text-green-400">{formatCurrency(selectedPayout.total_pending)} MXN</span>
              </div>
            </div>

            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mb-6">
              <p className="verdana-regular text-purple-200 text-sm">
                ℹ️ Este pago se marcará como procesado y se registrará en el historial.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowPayoutModal(false);
                  setSelectedPayout(null);
                }}
                variant="outline"
                className="flex-1 border-purple-500/50 text-white hover:bg-purple-500/20"
                data-testid="cancel-payout"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => handleProcessPayout(selectedPayout, selectedPayout.payment_method)}
                className="flex-1 btn-viubux"
                data-testid="confirm-payout"
              >
                Confirmar Pago
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutManagement;
